import { CalendarDays } from "lucide-react";

import type { EncounterListItem } from "@elation/api-client/src/stitch-web/models";

import { EncounterEntity } from "@src/features/encounters";
import { MedicalCodeEntity } from "@src/features/medical-codes";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Badge } from "@src/shared/components/ui/badge";
import { Card, CardContent, CardHeader } from "@src/shared/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@src/shared/components/ui/table";

export interface PatientLatestVisitMedicalCodeV2 {
  id: string;
  code: string;
  description?: string | null;
  groupLabel: string;
}

export interface PatientLatestVisitV2Props {
  latestEncounter: EncounterListItem;
  noteText?: string | null;
  medicalCodes?: PatientLatestVisitMedicalCodeV2[];
  isNoteLoading?: boolean;
  isNoteError?: boolean;
  noteErrorMessage?: string | null;
  isLoadingCodes?: boolean;
  isCodesError?: boolean;
}

function PatientLatestVisitHeader({
  latestEncounter,
}: {
  latestEncounter: EncounterListItem;
}) {
  const visitDate = EncounterEntity.visitDateLabel(latestEncounter);
  const physicianName = EncounterEntity.physicianDisplayName(latestEncounter);

  return (
    <HorizontalFlexbox
      alignItems="flex-start"
      justifyContent="space-between"
      flexWrap="wrap"
      className="gap-3"
    >
      <VerticalFlexbox className="min-w-0 gap-1">
        <HorizontalFlexbox alignItems="center" className="gap-2">
          <h2 className="text-[18px] font-semibold tracking-[-0.02em] text-surface-foreground">
            Past visit
          </h2>
          <Badge variant="outline" className="font-mono text-[10px]">
            {latestEncounter.status}
          </Badge>
        </HorizontalFlexbox>
        <p className="text-sm leading-5 text-muted-foreground">
          {EncounterEntity.notePreview(latestEncounter) ??
            "No note preview available."}
        </p>
      </VerticalFlexbox>

      <VerticalFlexbox className="items-start gap-1 md-stitch:items-end">
        {visitDate ? (
          <HorizontalFlexbox
            alignItems="center"
            className="gap-1.5 font-mono text-[11px] text-muted-foreground"
          >
            <CalendarDays className="size-3.5" aria-hidden="true" />
            {visitDate}
          </HorizontalFlexbox>
        ) : null}
        {physicianName ? (
          <p className="font-mono text-[11px] text-muted-foreground">
            {physicianName}
          </p>
        ) : null}
      </VerticalFlexbox>
    </HorizontalFlexbox>
  );
}

function PatientLatestVisitCodes({
  codes,
  isLoading,
  isError,
}: {
  codes: PatientLatestVisitMedicalCodeV2[];
  isLoading?: boolean;
  isError?: boolean;
}) {
  return (
    <Card className="overflow-hidden rounded-[10px] shadow-none">
      {codes.length > 0 ? (
        <Table aria-label="Billing codes">
          <TableBody>
            {codes.map((code) => (
              <TableRow key={code.id}>
                <TableCell className="w-24 px-4 py-3 font-mono text-[12.5px] font-semibold text-foreground">
                  {code.code}
                </TableCell>
                <TableCell className="px-4 py-3 text-[12.5px] text-muted-foreground">
                  {MedicalCodeEntity.descriptionLabel(
                    code,
                    "Suggested billing code",
                  )}
                </TableCell>
                <TableCell className="w-24 px-4 py-3 text-right">
                  <Badge
                    variant="outline"
                    className="font-mono text-[10px] text-muted-foreground"
                  >
                    {code.groupLabel}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : isError ? (
        <p className="px-4 py-3 text-[13px] text-destructive">
          Unable to load billing codes.
        </p>
      ) : isLoading && codes.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">
          Loading billing codes...
        </p>
      ) : (
        <p className="px-4 py-3 text-[13px] text-muted-foreground">
          No billing codes were detected for this visit.
        </p>
      )}
    </Card>
  );
}

export function PatientLatestVisitV2({
  latestEncounter,
  noteText,
  medicalCodes = [],
  isNoteLoading = false,
  isNoteError = false,
  noteErrorMessage = null,
  isLoadingCodes = false,
  isCodesError = false,
}: PatientLatestVisitV2Props) {
  const hasNoteText = Boolean(noteText?.trim());
  const notePreview = EncounterEntity.notePreview(latestEncounter);
  const hasNotePreview = Boolean(notePreview);
  const noteContent = hasNoteText
    ? noteText
    : hasNotePreview
      ? notePreview
      : isNoteError
        ? (noteErrorMessage ??
          "Unable to load the completed note for this visit.")
        : isNoteLoading
          ? "Loading visit note..."
          : "No completed note is available for this visit.";

  return (
    <Card className="rounded-card bg-surface shadow-none">
      <CardHeader className="p-card pb-3">
        <PatientLatestVisitHeader latestEncounter={latestEncounter} />
      </CardHeader>
      <CardContent className="px-card pb-card pt-0">
        <VerticalFlexbox className="gap-4">
          <VerticalFlexbox className="gap-2">
            <h3 className="font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              Visit note
            </h3>
            <Card className="min-h-[160px] rounded-[10px] bg-background shadow-none">
              <CardContent className="whitespace-pre-wrap px-4 py-3 text-[13px] leading-relaxed text-foreground">
                {noteContent}
              </CardContent>
            </Card>
          </VerticalFlexbox>

          <section aria-labelledby="patient-detail-billing-codes-heading">
            <VerticalFlexbox className="gap-2">
              <h3
                id="patient-detail-billing-codes-heading"
                className="font-mono text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground"
              >
                Billing codes
              </h3>
              <PatientLatestVisitCodes
                codes={medicalCodes}
                isLoading={isLoadingCodes}
                isError={isCodesError}
              />
            </VerticalFlexbox>
          </section>
        </VerticalFlexbox>
      </CardContent>
    </Card>
  );
}
